import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme } from "next-themes";
import { useUsersDashboard, useUsersList } from "../../hooks/useApi";
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Ban,
  Mail,
  Users,
  Activity,
  Heart,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import AdminUserDetail from "./AdminUserDetail";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Label,
  LabelList,
} from "recharts";

const statusStyles: Record<string, string> = {
  active: "bg-primary/10 text-primary",
  inactive: "bg-muted text-muted-foreground",
  suspended: "bg-destructive/10 text-destructive",
};

const GENDER_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-4))",
];

const DIABETES_COLORS = [
  "hsl(var(--destructive))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-2))",
  "hsl(var(--muted-foreground))",
];

const DIET_COLORS = [
  "hsl(var(--chart-2))",
  "hsl(var(--primary))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
];

const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  color: "hsl(var(--foreground))",
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  
  // Always read date from URL as source of truth, default to today if not present
  const urlDate = searchParams.get('date');
  const selectedDate = urlDate ? dayjs(urlDate) : dayjs();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(5);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Handle date change from DatePicker
  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSearchParams({ date: date.format("YYYY-MM-DD") });
    } else {
      setSearchParams({ date: dayjs().format("YYYY-MM-DD") });
    }
  };

  const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined;

  const { data: usersDashboard, isLoading: isDashboardLoading } = useUsersDashboard(dateParam);

  // Users list from API
  const { data: usersListData, isLoading: isUsersLoading } = useUsersList(dateParam);
  const apiUsers = usersListData?.results ?? [];

  // Frontend filtering based on engagement_status and search
  const filteredUsers = apiUsers.filter(user => {
    // Search filter (name or email)
    const matchesSearch = 
      user.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Status filter based on engagement_status field
    const matchesStatus = 
      statusFilter === "all" || 
      user.engagement_status?.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const visibleUsers = filteredUsers.slice(0, visibleCount);

  // Metrics from API
  const apiMetrics = usersDashboard?.metrics ?? [];
  const totalUsersCount = apiMetrics.find(m => m.id === "total-users")?.value;
  const activeUsersCount = apiMetrics.find(m => m.id === "active-users")?.value;
  const fitbitCount = apiMetrics.find(m => m.id === "fitbit")?.value;
  const avgBBSScore = apiMetrics.find(m => m.id === "avg-bbs")?.value;

  // Status counts from API
  const statusCounts = usersDashboard?.status_counts ?? { total_users: 0, active_users: 0, inactive_users: 0 };

  // Chart data — pure API, no fallback
  const genderData = (usersDashboard?.gender_distribution ?? []).reduce((acc, item) => {
    const existing = acc.find(e => e.name === item.name);
    if (existing) {
      existing.count += item.count;
      existing.percentage = parseFloat((existing.percentage + item.percentage).toFixed(1));
      existing.value = parseFloat((existing.value + item.value).toFixed(1));
    } else {
      acc.push({ ...item });
    }
    return acc;
  }, [] as NonNullable<typeof usersDashboard>["gender_distribution"]);
  const raceData = (usersDashboard?.race_breakdown ?? []).map(d => ({ race: d.name, users: d.count, percentage: d.percentage }));
  const diabetesData = (usersDashboard?.diabetes_breakdown ?? []).reduce((acc, item) => {
    const existing = acc.find(e => e.name === item.name);
    if (existing) {
      existing.count += item.count;
      existing.percentage = parseFloat((existing.percentage + item.percentage).toFixed(1));
      existing.value = parseFloat((existing.value + item.value).toFixed(1));
    } else {
      acc.push({ ...item });
    }
    return acc;
  }, [] as NonNullable<typeof usersDashboard>["diabetes_breakdown"]);
  const activityData = (usersDashboard?.activity_level ?? []).map(d => ({ level: d.subject, value: d.A, fullMark: d.fullMark }));
  const dietData = usersDashboard?.diet_preferences ?? [];
  const ageData = (usersDashboard?.age_distribution ?? []).map(d => ({ range: d.age, users: d.value }));
  const bmiData = (usersDashboard?.bmi_distribution ?? []).map(d => ({ range: d.name, users: d.value }));

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">User Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            View and manage all BiteBalance users
          </p>
        </div>
        <ConfigProvider
          theme={{
            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: {
              colorPrimary: isDark ? "hsl(221, 83%, 53%)" : "hsl(221, 65%, 58%)",
              colorBgContainer: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorBgElevated: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorText: isDark ? "hsl(210, 40%, 98%)" : "hsl(240, 10%, 10%)",
              colorTextPlaceholder: isDark ? "hsl(215, 20%, 65%)" : "hsl(240, 4%, 46%)",
              colorBorder: isDark ? "hsl(224, 71%, 15%)" : "hsl(240, 6%, 90%)",
              borderRadius: 8,
            },
          }}
        >
          <DatePicker
            format="MMM D, YYYY"
            value={selectedDate}
            onChange={handleDateChange}
            allowClear
          />
        </ConfigProvider>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Total Users
                </p>
                <p className="text-2xl font-bold text-foreground">{totalUsersCount?.toLocaleString() ?? "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Active Users
                </p>
                <p className="text-2xl font-bold text-primary">{activeUsersCount?.toLocaleString() ?? "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Heart className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Avg Bite Balance
                </p>
                <p className="text-2xl font-bold text-foreground">{avgBBSScore ?? "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Fitbit Connected
                </p>
                <p className="text-2xl font-bold text-primary">{fitbitCount?.toLocaleString() ?? "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demographics Row 1: Gender + Race */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Gender Distribution</CardTitle>
            <CardDescription>Male / Female / Other split</CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-48 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : genderData.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={genderData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={4}
                        dataKey="count"
                      >
                        {genderData.map((_, index) => (
                          <Cell
                            key={`gender-${index}`}
                            fill={GENDER_COLORS[index % GENDER_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number) => [`${value} users`, "Count"]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value, entry: any) => `${entry.payload.name}: ${entry.payload.count}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-1.5">
                  {genderData.map((item, index) => (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: GENDER_COLORS[index] }}
                      />
                      <span className="text-muted-foreground">{item.name}</span>
                      <span className="ml-auto font-semibold text-foreground">
                        {item.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Race / Ethnicity Breakdown
            </CardTitle>
            <CardDescription>
              User distribution by race from onboarding assessment
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-72 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : raceData.length === 0 ? (
              <div className="flex h-72 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={raceData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      type="number"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                      label={{ value: 'Users', position: 'insideBottom', offset: -5, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      dataKey="race"
                      type="category"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 11,
                      }}
                      width={210}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, _name: string, props: any) => [
                        `${value} users (${props.payload.percentage}%)`,
                        "Count"
                      ]}
                    />
                    <Bar
                      dataKey="users"
                      fill="hsl(var(--primary))"
                      radius={[0, 6, 6, 0]}
                    >
                      <LabelList dataKey="users" position="right" fill="hsl(var(--foreground))" fontSize={11} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Demographics Row 2: Age + Diabetes Type */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Age Distribution</CardTitle>
            <CardDescription>
              User age groups from date of birth
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : ageData.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ageData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="range"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                      label={{ value: 'Age Range', position: 'insideBottom', offset: -5, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                      label={{ value: 'Users', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number) => [`${value} users`, "Count"]}
                    />
                    <Bar
                      dataKey="users"
                      fill="hsl(var(--chart-2))"
                      radius={[6, 6, 0, 0]}
                    >
                      <LabelList dataKey="users" position="top" fill="hsl(var(--foreground))" fontSize={11} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Diabetes Type Breakdown</CardTitle>
            <CardDescription>
              User-reported diabetes status from assessment
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-44 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : diabetesData.length === 0 ? (
              <div className="flex h-44 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={diabetesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={4}
                        dataKey="count"
                      >
                        {diabetesData.map((_, index) => (
                          <Cell
                            key={`diabetes-${index}`}
                            fill={DIABETES_COLORS[index % DIABETES_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number, _name: string, props: any) => [`${value} users`, props.payload.name]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value, entry: any) => `${entry.payload.name}: ${entry.payload.count}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {diabetesData.map((item, index) => (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: DIABETES_COLORS[index] }}
                      />
                      <span className="text-muted-foreground truncate">{item.name}</span>
                      <span className="ml-auto font-semibold text-foreground whitespace-nowrap">
                        {item.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Demographics Row 3: Activity Level + Diet Type + BMI */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Activity Level</CardTitle>
            <CardDescription>
              Self-reported activity from onboarding
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-52 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : activityData.length === 0 ? (
              <div className="flex h-52 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    data={activityData}
                    cx="50%"
                    cy="50%"
                    outerRadius="70%"
                  >
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis
                      dataKey="level"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 10,
                      }}
                    />
                    <PolarRadiusAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 10,
                      }}
                    />
                    <Radar
                      name="Users"
                      dataKey="value"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string, props: any) => [`${value} users`, props.payload.level]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={20}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Diet Preferences</CardTitle>
            <CardDescription>Dietary choices from assessment</CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-44 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : dietData.length === 0 ? (
              <div className="flex h-44 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dietData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={4}
                        dataKey="count"
                      >
                        {dietData.map((_, index) => (
                          <Cell key={`diet-${index}`} fill={DIET_COLORS[index % DIET_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number, _name: string, props: any) => [`${value} users`, props.payload.name]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value, entry: any) => `${entry.payload.name}: ${entry.payload.count}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-1.5">
                  {dietData.map((item, index) => (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: DIET_COLORS[index] }}
                      />
                      <span className="text-muted-foreground">{item.name}</span>
                      <span className="ml-auto font-semibold text-foreground">
                        {item.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">BMI Distribution</CardTitle>
            <CardDescription>
              Calculated from height & weight data
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDashboardLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : bmiData.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bmiData} margin={{ top: 25, right: 20, bottom: 30, left: 20 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="range"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 11,
                      }}
                      height={60}
                      interval={0}
                      angle={0}
                      label={{ 
                        value: 'BMI Range', 
                        position: 'insideBottom', 
                        offset: -15, 
                        fill: 'hsl(var(--muted-foreground))',
                        fontSize: 12
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                      label={{ 
                        value: 'Users', 
                        angle: -90, 
                        position: 'insideLeft', 
                        fill: 'hsl(var(--muted-foreground))',
                        fontSize: 12
                      }}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number) => [`${value} users`, "Count"]}
                    />
                    <Bar
                      dataKey="users"
                      fill="hsl(var(--chart-3))"
                      radius={[6, 6, 0, 0]}
                    >
                      <LabelList dataKey="users" position="top" fill="hsl(var(--foreground))" fontSize={12} fontWeight="600" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Users</CardTitle>
          <CardDescription>
            Browse and search through registered users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="mr-2 h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Status {statusCounts.total_users > 0 && `(${statusCounts.total_users})`}
                </SelectItem>
                <SelectItem value="active">
                  Active {statusCounts.active_users > 0 && `(${statusCounts.active_users})`}
                </SelectItem>
                <SelectItem value="inactive">
                  Inactive {statusCounts.inactive_users > 0 && `(${statusCounts.inactive_users})`}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="hidden font-semibold md:table-cell">
                    Joined
                  </TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="hidden font-semibold lg:table-cell">
                    Meals Logged
                  </TableHead>
                  <TableHead className="hidden font-semibold lg:table-cell">
                    Fitbit
                  </TableHead>
                  <TableHead className="hidden font-semibold lg:table-cell">
                    BBS
                  </TableHead>
                  <TableHead className="hidden font-semibold lg:table-cell">
                    Glucose Spike
                  </TableHead>

                  <TableHead className="w-10 font-semibold" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isUsersLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="h-24 text-center text-muted-foreground"
                    >
                      No users found matching the filters
                    </TableCell>
                  </TableRow>
                ) : visibleUsers.map(user => (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                          {user.user
                            .split(" ")
                            .map(n => n[0])
                            .join("")}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {user.user}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                      {new Date(user.joined).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`${statusStyles[user.engagement_status?.toLowerCase() ?? "inactive"] ?? "bg-muted text-muted-foreground"} border-0 text-xs capitalize`}
                      >
                        {user.engagement_status || "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden text-sm font-medium text-foreground lg:table-cell">
                      {user.meals_logged ?? "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge
                        variant="secondary"
                        className={`border-0 text-xs ${
                          user.fitbit_status?.toLowerCase() === "connected"
                            ? "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-700"
                            : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                        }`}
                      >
                        {user.fitbit_status || "Not Connected"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {user.bbs != null ? (
                        <span
                          className={`text-sm font-bold ${
                            user.bbs >= 80
                              ? "text-emerald-600"
                              : user.bbs >= 60
                                ? "text-primary"
                                : user.bbs >= 40
                                  ? "text-amber-500"
                                  : "text-destructive"
                          }`}
                        >
                          {user.bbs}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {user.glucose_spike ? (
                        <Badge
                          variant="secondary"
                          className="border-0 text-xs bg-amber-500/10 text-amber-600"
                        >
                          {user.glucose_spike}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="gap-2">
                            <Eye className="h-4 w-4" /> View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-2">
                            <Mail className="h-4 w-4" /> Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-2 text-destructive">
                            <Ban className="h-4 w-4" /> Suspend User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* View More */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Showing {Math.min(visibleCount, filteredUsers.length)} of {filteredUsers.length} users
            </p>
            {visibleCount < filteredUsers.length && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisibleCount(v => v + 10)}
              >
                View More
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId(null)}>
        <DialogContent className="max-w-[98vw] w-[1400px] max-h-[98vh] overflow-y-auto p-0">
          {selectedUserId && <AdminUserDetail userId={selectedUserId} dateParam={dateParam} isModal />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
